import { PropsWithChildren, useEffect } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { FormattedMessage } from 'react-intl';

import BackIcon from '@/assets/icons/back.svg';
import ErrorIcon from '@/assets/icons/error.svg';
import ReloadIcon from '@/assets/icons/reload.svg';
import { Typography } from '@/components/ui/typography';
import { reportBoundaryError } from '@/utils/sentry';

import { Button } from './button';

export function ErrorState({
  message,
  onRetry,
}: { message?: string; onRetry?: () => void } = {}) {
  if (message)
    return (
      <div
        role="alert"
        className="flex min-w-0 flex-wrap items-center justify-end gap-3 sm:flex-nowrap"
      >
        <Typography variant="small1" className="text-fx-rose-800">
          {message}
        </Typography>
        <Button
          variant="secondary"
          size="sm"
          className="shrink-0"
          onClick={onRetry ?? (() => window.location.reload())}
        >
          <FormattedMessage
            defaultMessage="Try again"
            description="Retry loading data after a failed request"
          />
        </Button>
      </div>
    );
  return <PageErrorState />;
}

function PageErrorState() {
  const goBack = () => {
    window.history.back();
  };

  useEffect(() => {
    const handlePopState = () => {
      window.location.reload();
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const reloadPage = () => {
    window.location.reload();
  };

  return (
    <div className="mx-auto flex w-[300px] flex-1 flex-col items-center justify-center">
      <img src={ErrorIcon} alt="" />
      <Typography variant="body2">
        <FormattedMessage
          defaultMessage="Oops ! Something went wrong."
          description="Short error title in generic error state view"
        />
      </Typography>
      <Typography variant="subtitle1" className="text-center">
        <FormattedMessage
          defaultMessage="We encountered an unexpected error. Please try refreshing the page, or contact support if the problem persists"
          description="Generic error boundary message for failed data loads in Source"
        />
      </Typography>
      <div className="my-4 flex items-center justify-center gap-2">
        <Button variant="secondary" size="sm" onClick={goBack}>
          <img src={BackIcon} alt="" />
          <FormattedMessage
            defaultMessage="Back"
            description="Error UI: navigate to previous route"
          />
        </Button>
        <Button variant="secondary" size="sm" onClick={reloadPage}>
          <img src={ReloadIcon} alt="" />
          <FormattedMessage
            defaultMessage="Reload"
            description="Error UI: full page refresh to retry"
          />
        </Button>
      </div>
    </div>
  );
}

export function ErrorStateFallback({ children }: PropsWithChildren) {
  return (
    <ErrorBoundary
      onError={reportBoundaryError('page')}
      fallback={<ErrorState />}
    >
      {children}
    </ErrorBoundary>
  );
}
